/**
 * SidebarStep7_FutureClosure - Setup Review & Hand-off (Future Closure Path)
 *
 * Wizard v2 is where the DATA SPECIALIST sets up and VERIFIES the case — it is not
 * an analysis surface. Per the 2026-07-08 architecture decision, the wizard shows NO
 * deterministic total: the phased pro-forma, sensitivity/Monte Carlo, court exhibits,
 * and the demand all live on the ACCOUNTANT dashboard (computed on the verified case).
 *
 * This final step:
 *   - Confirms the case is correctly configured (identity, closure timing, financials,
 *     forced-closure evidence).
 *   - Hands the case off to the accountant (status → pending_accountant_review).
 *   - No numbers, no Monte Carlo, no policy-limit check — those are downstream analysis.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/TestAuthContext';
import axios from '@/lib/http';
import WizardV2Layout from './WizardV2Layout';
import type { QAItem } from './WizardSidebar';
import { useActivityLog } from '../../../hooks/useActivityLog';
import { formatCurrency } from '@/utils/financialFormat';
import {
  CheckCircle,
  FileText,
  Building,
  Calendar,
  AlertTriangle,
  Loader2,
  ArrowRight,
  ClipboardCheck,
  Wrench,
  Megaphone,
  TrendingDown,
} from 'lucide-react';

interface SidebarStep7FutureClosureProps {
  onBack: () => void;
  onComplete: () => void;
  goToStep?: (step: number) => void;
  data: any;
  onSave?: (explicitData?: any) => Promise<void>;
  isSaving?: boolean;
  lastSaved?: Date | null;
  highestCompletedStep?: number;
  backendCompletionPercent?: number;
  claimId?: string;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SidebarStep7_FutureClosure({
  onBack,
  onComplete,
  goToStep,
  data,
  onSave,
  isSaving,
  lastSaved,
  highestCompletedStep,
  backendCompletionPercent,
  claimId,
}: SidebarStep7FutureClosureProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { getToken } = useAuth();
  const { logActivity } = useActivityLog();
  const navigate = useNavigate();

  const reportId = data.report_id || data.generated_report_id;
  const claimantName = data.claimant_name || data.business_name || data.client_name || 'Unknown Business';
  const eventType = data.event_type || 'Forced Closure';
  const closureDuration = data.closure_duration_months || data.future_closure_params?.closure_duration_months;
  const eventStart = data.event_start_date;

  // Setup-completeness signals (the specialist's verification — NOT an analysis result).
  const hasFinancials =
    (data.unified_upload_data?.canonical_data?.periods?.length || 0) > 0 ||
    !!data.financial_data ||
    Object.keys(data).some((k) => k.startsWith('data_20'));
  const hasClosureTiming = !!eventStart && !!closureDuration;

  // Forced-closure evidence (informational — optional, entered in Step 5)
  const propertyCost = data.fc_property_replacement?.cost;
  const marketingCost = data.fc_marketing_icw?.cost;
  const permanentClosure = !!data.fc_dbv?.permanent_closure;

  // Ready to hand off when the case is configured (timing + financials present).
  const setupComplete = hasFinancials && hasClosureTiming;

  const handleSubmit = async () => {
    if (!setupComplete) {
      setSubmitError('Case setup is incomplete — confirm closure timing (Step 2) and uploaded financials (Step 3) before hand-off.');
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const token = await getToken();
      const targetId = claimId || reportId;
      if (targetId) {
        // Hand off to the accountant for forensic analysis. The wizard does NOT
        // compute or persist a demand — the accountant dashboard runs the phased engine.
        await axios.put(
          `${BACKEND_URL}/api/v1/reports/${targetId}/update`,
          {
            status: 'pending_accountant_review',
            submitted_at: new Date().toISOString(),
            calculation_mode: 'future_closure',
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      logActivity({
        reportId: reportId || 'unknown',
        actionType: 'submit',
        actionDetails: `Handed off Future Closure case for ${claimantName} to accountant review`,
        section: 'wizard',
        metadata: { calculation_mode: 'future_closure', closure_duration: closureDuration },
      });
      setIsSubmitted(true);
    } catch (err: any) {
      console.error('Hand-off failed:', err);
      setSubmitError(err.response?.data?.detail || 'Failed to hand off the case. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const buildSections = () => [
    { id: 'checklist', label: 'Setup Checklist', isActive: true, isComplete: setupComplete, onClick: () => document.getElementById('checklist-section')?.scrollIntoView({ behavior: 'smooth' }) },
    { id: 'handoff', label: 'Hand-off', isActive: false, isComplete: false, onClick: () => document.getElementById('handoff-section')?.scrollIntoView({ behavior: 'smooth' }) },
  ];

  const buildQAItems = (): QAItem[] => {
    const items: QAItem[] = [];
    items.push({ id: 'timing', label: hasClosureTiming ? 'Closure timing set' : 'Closure timing missing', status: hasClosureTiming ? 'complete' : 'warning' });
    items.push({ id: 'financials', label: hasFinancials ? 'Financials uploaded' : 'Financials missing', status: hasFinancials ? 'complete' : 'warning' });
    return items;
  };

  // Verification row
  const CheckRow = ({ ok, icon: Icon, label, value, optional }: { ok: boolean; icon: any; label: string; value: string; optional?: boolean }) => (
    <div className="flex items-center gap-3 p-4 rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: ok ? 'transparent' : 'var(--color-bg-surface)' }}>
        {ok ? <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
            : <AlertTriangle className="w-5 h-5" style={{ color: optional ? 'var(--color-text-muted)' : 'var(--color-warning, #b45309)' }} />}
      </div>
      <Icon className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--color-text-dim)' }} />
      <div className="flex-1 min-w-0">
        <p className="cs-label">{label}{optional ? ' (optional)' : ''}</p>
        <p className="cs-body truncate" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{value}</p>
      </div>
    </div>
  );

  if (isSubmitted) {
    return (
      <WizardV2Layout
        currentStep={7}
        highestCompletedStep={highestCompletedStep}
        stepTitle="Handed Off to Accountant"
        stepSubtitle="Your verified case is queued for forensic analysis"
        completionPercent={backendCompletionPercent ?? 100}
        grade="A"
        calculationMode="future_closure"
        sections={buildSections()}
        qaItems={[{ id: 'submitted', label: 'Case handed off', status: 'complete' }]}
        isSaving={false}
        lastSaved={lastSaved}
        onSave={onSave}
        goToStep={goToStep}
        onBack={onBack}
        showNext={false}
        claimId={claimId}
      >
        <div className="p-6 lg:p-8">
          <div className="max-w-2xl mx-auto text-center py-12">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: 'transparent' }}>
              <CheckCircle className="w-10 h-10" style={{ color: 'var(--color-verified)' }} />
            </div>
            <h2 className="cs-display mb-4">Case Handed Off</h2>
            <p className="cs-body mb-8">
              The Future Closure case for <strong>{claimantName}</strong> is verified and queued for the
              accountant's forensic analysis — the phased pro-forma, sensitivity, and court exhibits run there.
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate('/')}
                className="cs-body px-8 py-3 rounded-xl font-semibold transition-colors flex items-center gap-2"
                style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                View Dashboard <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={onComplete}
                className="cs-body px-8 py-3 rounded-xl font-semibold transition-colors"
                style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
              >
                Create Another Claim
              </button>
            </div>
          </div>
        </div>
      </WizardV2Layout>
    );
  }

  return (
    <WizardV2Layout
      currentStep={7}
      highestCompletedStep={highestCompletedStep}
      stepTitle="Setup Review & Hand-off"
      stepSubtitle="Verify the case is configured, then hand off to the accountant for analysis"
      completionPercent={backendCompletionPercent ?? 100}
      grade="A"
      calculationMode="future_closure"
      sections={buildSections()}
      qaItems={buildQAItems()}
      isSaving={isSaving}
      lastSaved={lastSaved}
      onSave={onSave}
      goToStep={goToStep}
      onBack={onBack}
      showNext={false}
      claimId={claimId}
    >
      <div className="p-6 lg:p-8 space-y-6">

        {/* What happens next (sets expectation: wizard = setup, accountant = analysis) */}
        <div className="rounded-xl p-6" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--color-bg-surface)' }}>
              <ClipboardCheck className="w-6 h-6" style={{ color: 'var(--color-brand)' }} />
            </div>
            <div>
              <h2 className="cs-h2" style={{ color: 'var(--color-needs-human-text)' }}>Verify setup, then hand off</h2>
              <p className="cs-body" style={{ color: 'var(--color-needs-human-text)' }}>
                This wizard configures and verifies the case. The <strong>accountant</strong> runs the full
                forensic analysis — deterministic phased pro-forma, sensitivity, and court-ready exhibits — on
                your verified data. No demand figure is produced here.
              </p>
            </div>
          </div>
        </div>

        {/* Setup checklist */}
        <div id="checklist-section" className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)' }}>
              <FileText className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
            </div>
            <h3 className="cs-h3">Case Setup Checklist</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CheckRow ok={!!claimantName && claimantName !== 'Unknown Business'} icon={Building} label="Business" value={claimantName} />
            <CheckRow ok={!!eventType} icon={AlertTriangle} label="Event Type" value={eventType} />
            <CheckRow ok={hasClosureTiming} icon={Calendar} label="Closure Timing"
              value={hasClosureTiming ? `${eventStart} · ${closureDuration} months` : 'Set start date + duration (Step 2)'} />
            <CheckRow ok={hasFinancials} icon={FileText} label="Financials"
              value={hasFinancials ? 'Uploaded & parsed' : 'Upload financials (Step 3)'} />
            <CheckRow ok={propertyCost > 0} optional icon={Wrench} label="Property Replacement"
              value={propertyCost ? formatCurrency(propertyCost) : 'None entered'} />
            <CheckRow ok={marketingCost > 0} optional icon={Megaphone} label="Marketing / ICW"
              value={marketingCost ? formatCurrency(marketingCost) : 'None entered'} />
            {permanentClosure && (
              <CheckRow ok icon={TrendingDown} label="Permanent Closure" value="Diminished business value applies" />
            )}
          </div>
        </div>

        {/* Error */}
        {submitError && (
          <div className="rounded-xl p-4 flex items-start gap-3" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
            <AlertTriangle className="w-5 h-5 mt-0.5" style={{ color: 'var(--color-brand)' }} />
            <div>
              <p className="cs-body" style={{ color: 'var(--color-needs-human-text)', fontWeight: 600 }}>Hand-off blocked</p>
              <p className="cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>{submitError}</p>
            </div>
          </div>
        )}

        {/* Hand-off */}
        <div id="handoff-section" className="pt-2">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !setupComplete}
            title={!setupComplete ? 'Complete closure timing (Step 2) and financials (Step 3) first' : undefined}
            className="cs-body w-full font-bold py-4 px-8 rounded-xl shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            style={{ fontSize: '1.125rem', background: 'var(--color-verified)', color: 'var(--color-on-brand)' }}
          >
            {isSubmitting ? (
              <><Loader2 className="w-6 h-6 animate-spin" /> Handing off…</>
            ) : (
              <><CheckCircle className="w-6 h-6" /> Hand Off to Accountant</>
            )}
          </button>
          <p className="cs-meta text-center mt-3">
            Saves the verified case and queues it for the accountant's forensic analysis.
          </p>
        </div>

      </div>
    </WizardV2Layout>
  );
}
